import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FindCategoryQuiz from "@/components/FindCategoryQuiz";
import { ArrowRight, ShieldCheck, Zap, Users, Star, Wrench, Truck, Laptop, Wifi, Tv, Hammer, Leaf, Sparkles, Droplet, Paintbrush, Home as HomeIcon, Wind, Square, Grid, Grid3x3, DoorClosed, DoorOpen, Lock, Bug, SprayCan, CloudRain, Trash2, Flame, Scissors, TreePine, Waves, Fence, Utensils, Palette, Speaker, Shield, Sun, Car, Camera, Video, Music, Calendar, Dumbbell, Book, Code, Megaphone, Calculator, Dog, PawPrint, Compass } from "lucide-react";

const ICONS = {
  droplet: Droplet, zap: Zap, wind: Wind, truck: Truck, laptop: Laptop, wifi: Wifi,
  tv: Tv, speaker: Speaker, leaf: Leaf, sparkles: Sparkles, home: HomeIcon,
  paintbrush: Paintbrush, hammer: Hammer, saw: Hammer, wrench: Wrench,
  square: Square, grid: Grid, "grid-3x3": Grid3x3, "door-closed": DoorClosed, "door-open": DoorOpen,
  lock: Lock, bug: Bug, "spray-can": SprayCan, "cloud-rain": CloudRain, "trash-2": Trash2,
  flame: Flame, scissors: Scissors, "tree-pine": TreePine, waves: Waves, fence: Fence,
  utensils: Utensils, palette: Palette, shield: Shield, sun: Sun, car: Car,
  camera: Camera, video: Video, music: Music, calendar: Calendar, dumbbell: Dumbbell,
  book: Book, code: Code, megaphone: Megaphone, calculator: Calculator, dog: Dog, pawprint: PawPrint,
};

export default function Landing() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [quizOpen, setQuizOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const startAsCustomer = () => navigate(user?.role === "customer" ? "/customer/post" : "/auth?mode=register&role=customer");
  const startAsPro = () => navigate(user?.role === "contractor" ? "/contractor" : "/auth?mode=register&role=contractor");

  const marqueeItems = [...categories, ...categories];

  return (
    <div className="min-h-screen bg-bone">
      <Seo
        path="/"
        description="Post your home project free on LeadTitan and get matched with vetted local trade pros in minutes. Pros pay only for the leads they want."
      />
      <Navbar />

      {/* Hero */}
      <section className="border-b-2 border-ink relative overflow-hidden">
        <div className="grid-lines absolute inset-0 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 relative">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="overline bg-acid border-2 border-ink px-3 py-1">// EST. 2026</span>
              <span className="overline">Vetted Pros. Real Leads.</span>
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9] tracking-tighter">
              Hire a pro.
              <br />
              <span className="bg-ink text-acid px-2 inline-block">Win the job.</span>
              <br />
              No middlemen.
            </h1>
            <p className="text-lg text-muted-foreground mt-8 max-w-xl">
              The high-signal lead network for plumbers, electricians, movers, tech installers and everything in between. Customers post. Pros bid. We charge per lead — no commissions.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button onClick={startAsCustomer} className="brut-btn-primary text-sm" data-testid="hero-find-pro">
                Find a Pro <ArrowRight className="w-4 h-4 ml-2" />
              </button>
              <button onClick={startAsPro} className="brut-btn-acid text-sm" data-testid="hero-join-pro">
                Join as a Pro <ArrowRight className="w-4 h-4 ml-2" />
              </button>
              <button onClick={() => setQuizOpen(true)} className="brut-btn text-sm" data-testid="hero-help-me-choose">
                <Compass className="w-4 h-4 mr-2" /> Not sure? Help me pick
              </button>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-4 max-w-xl">
              <Stat n={categories.length || "—"} label="Trade categories" />
              <Stat n="$8+" label="Lead price avg" />
              <Stat n="24/7" label="Lead flow" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="brut-card aspect-[3/4] overflow-hidden">
                <img src="https://static.prod-images.emergentagent.com/jobs/a2bfb6c9-7dab-4e8a-b2f6-fb78dc6aa054/images/7216c063557ff57e30ecb31feccec4cbabb6b447a8f93b0723ed3d0eeffb9040.png" alt="Pro" className="w-full h-full object-cover grayscale contrast-125" />
              </div>
              <div className="space-y-4">
                <div className="brut-card p-6 bg-acid">
                  <div className="overline">Live leads</div>
                  <div className="font-display font-black text-5xl mt-2">237</div>
                  <div className="text-xs font-mono mt-2">▲ this week</div>
                </div>
                <div className="brut-card p-6 bg-klein text-white">
                  <div className="overline text-white/80">Avg job size</div>
                  <div className="font-display font-black text-4xl mt-2">$1,840</div>
                </div>
                <div className="brut-card p-4 bg-white">
                  <div className="flex items-center gap-1 text-acid">
                    {[...Array(5)].map((_, i) => <Star key={`hero-star-${i}`} className="w-4 h-4 fill-current stroke-ink" />)}
                  </div>
                  <p className="text-xs mt-2 font-mono">"Got 3 jobs in first week. ROI 12x." — Mike, HVAC pro</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="bg-acid border-b-2 border-ink overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-4">
          {marqueeItems.map((c, i) => (
            <span key={`marquee-${c.category_id}-${i}`} className="font-mono font-bold uppercase tracking-widest text-sm mx-8">
              {c.name} <span className="mx-2">★</span>
            </span>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-b-2 border-ink">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-7">
              <div className="overline mb-3">// How it works</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-none">
                Three steps.
                <br />Zero friction.
              </h2>
            </div>
            <p className="md:col-span-5 text-muted-foreground">
              Customers describe the job. Pros pay only for the leads they want. We keep the system honest with reviews and vetting.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-ink">
            <Step n="01" title="Post your project" body="Tell us your trade, location, and budget. Takes 60 seconds." />
            <Step n="02" title="Pros unlock the lead" body="Vetted local pros pay per lead or via subscription. You get matched fast." />
            <Step n="03" title="Hire & review" body="Compare quotes, hire your favorite, leave a public review. Done." />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="border-b-2 border-ink">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline mb-3">// Categories</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">Pick your trade</h2>
            </div>
            <Link to="/categories" className="brut-btn hidden md:inline-flex" data-testid="see-all-cats">
              See all <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0 border-2 border-ink">
            {categories.slice(0, 12).map((c) => {
              const Icon = ICONS[c.icon] || Wrench;
              return (
                <Link
                  to={`/trades/${c.slug}`}
                  key={c.category_id}
                  className="border-r-2 border-b-2 border-ink p-6 bg-white hover:bg-acid transition-colors group"
                  data-testid={`category-card-${c.slug}`}
                >
                  <Icon className="w-7 h-7 mb-4" />
                  <div className="font-display font-bold text-lg uppercase leading-tight">{c.name}</div>
                  <div className="font-mono text-xs mt-3 flex justify-between">
                    <span>${c.lead_price.toFixed(0)}/lead</span>
                    <span className="opacity-0 group-hover:opacity-100">→</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* For Pros */}
      <section className="border-b-2 border-ink bg-ink text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="overline text-acid mb-3">// For contractors</div>
            <h2 className="font-display font-black text-5xl uppercase tracking-tight leading-none">
              Two ways to pay.<br />
              <span className="text-acid">One way to win.</span>
            </h2>
            <p className="mt-6 text-white/70 max-w-lg">
              Pay per lead, or subscribe for monthly quota. No commissions. No surprises. Just clean, attributable ROI.
            </p>
            <Link to="/pricing" className="brut-btn-acid mt-8 inline-flex" data-testid="see-pricing">
              See pricing <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Feature icon={Zap} title="Real-time leads" body="New customer requests in your inbox within minutes." />
            <Feature icon={ShieldCheck} title="Vetted customers" body="Every project is reviewed for legitimacy before going live." />
            <Feature icon={Users} title="Direct connection" body="Customer name, email, and phone — full contact, no commissions." />
            <Feature icon={Star} title="Reviews matter" body="Build a permanent profile that wins more work over time." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b-2 border-ink">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 text-center">
          <div className="overline mb-4">// Ready when you are</div>
          <h2 className="font-display font-black text-5xl sm:text-7xl uppercase tracking-tighter">
            Start in <span className="bg-acid px-2">60 seconds.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <button onClick={startAsCustomer} className="brut-btn-primary" data-testid="cta-customer">I need a pro</button>
            <button onClick={startAsPro} className="brut-btn-acid" data-testid="cta-pro">I am a pro</button>
          </div>
        </div>
      </section>

      <Footer />
      {quizOpen && <FindCategoryQuiz onClose={() => setQuizOpen(false)} />}
    </div>
  );
}

const Stat = ({ n, label }) => (
  <div className="border-l-2 border-ink pl-4">
    <div className="font-display font-black text-3xl">{n}</div>
    <div className="font-mono text-[10px] uppercase tracking-widest mt-1">{label}</div>
  </div>
);

const Step = ({ n, title, body }) => (
  <div className="border-r-2 border-ink last:border-r-0 p-8 bg-white">
    <div className="font-mono text-acid bg-ink inline-block px-2 py-1 text-xs">{n}</div>
    <h3 className="font-display font-bold text-2xl uppercase mt-4 leading-tight">{title}</h3>
    <p className="text-sm text-muted-foreground mt-3">{body}</p>
  </div>
);

const Feature = ({ icon: Icon, title, body }) => (
  <div className="border-2 border-white/30 p-5 hover:border-acid transition-colors">
    <Icon className="w-6 h-6 text-acid" />
    <div className="font-display font-bold uppercase text-lg mt-3">{title}</div>
    <p className="text-sm text-white/60 mt-2">{body}</p>
  </div>
);
